import type { Omraade } from "#lib/types/omraade.js";
import type { Breadcrumb } from "#lib/types/breadcrumb.js";
import type { DayView } from "#lib/utils/spotprisenViews.js";

/**
 * Geographic intro per price area. Durable facts only — no market
 * commentary (drifts faster than dev edits).
 *
 * Note: the parenthetical region names ("Østlandet", "Sørlandet",
 * "Vestlandet") intentionally diverge from the area picker's formal
 * labels (Sørøst-Norge / Sørvest-Norge / Vest-Norge). The picker uses
 * Nord Pool's regulatory naming; these intros use the colloquial
 * regional names because they have much higher Norwegian search volume
 * — that's the whole point of putting them in the SEO copy.
 */
const AREA_INTROS: Record<string, string> = {
	NO1: "NO1 (Østlandet) er det største prisområdet i Norge og dekker Oslo, Akershus, Østfold, Vestfold, Innlandet, Buskerud og Telemark.",
	NO2: "NO2 (Sørlandet) dekker Agder og Rogaland og er knyttet til Europa via undersjøiske strømkabler.",
	NO3: "NO3 (Midt-Norge) dekker Trøndelag og Møre og Romsdal.",
	NO4: "NO4 (Nord-Norge) dekker Nordland, Troms og Finnmark. NO4 er fritatt merverdiavgift, så spotprisene som vises er reelle markedspriser uten 25 % mva-påslag.",
	NO5: "NO5 (Vestlandet) dekker Vestland fylke, inkludert Bergen-området.",
};

const DEFAULT_INTRO =
	"Norge er delt i fem prisområder. Velg område over for å se spotprisen i ditt nærområde.";

/** Geographic + market description for the area shown. URL-explicit area
 *  yields the area-specific intro; bare /spotprisen falls back to the
 *  generic "Norway has five price areas" copy. */
export const getAreaIntro = (area: Omraade | null): string =>
	area ? (AREA_INTROS[area.value] ?? DEFAULT_INTRO) : DEFAULT_INTRO;

/** Norwegian day label. `capitalized` for sentence-start uses (breadcrumb item). */
export const getDayLabel = (view: DayView, capitalized = false): string => {
	if (view === "i-gar") return capitalized ? "I går" : "i går";
	if (view === "i-morgen") return capitalized ? "I morgen" : "i morgen";
	return capitalized ? "I dag" : "i dag";
};

/** Page meta title. Carries both terms: "strømpris" for the high-volume
 *  search slot and "spotpris" as the accurate Nord Pool term. The tabell
 *  variant leads with "spotpris" (it literally lists spot prices); the
 *  chart variant leads with "strømpris".
 *  `area` is the resolved area (URL-explicit OR implicit default). */
export const getTitle = (view: DayView, area: Omraade, isTabell: boolean): string => {
	const day = getDayLabel(view);
	return isTabell
		? `Spotpris-tabell ${day} for ${area.label} – strømprisen time for time`
		: `Strømprisen ${day} for ${area.label} – spotprisen time for time`;
};

/** Meta description. NO4 (Nord-Norge) is fritatt mva — flag in the snippet. */
export const getDescription = (view: DayView, area: Omraade, isTabell: boolean): string => {
	const day = getDayLabel(view);
	const mvaNote = area.value === "NO4" ? " (NO4 er fritatt mva)" : "";
	return isTabell
		? `Strømprisen per time ${day} for ${area.label} – spotpris fra Nord Pool, inkl. mva${mvaNote}.`
		: `Se strømprisen ${day} for ${area.label} – markedsbasert spotpris fra Nord Pool, time for time, inkl. mva${mvaNote}.`;
};

/**
 * Breadcrumb trail. Forside → Spotprisen, plus a day-view crumb when the
 * URL has one (i-gar / i-morgen). Area is intentionally NOT in the trail
 * as its own crumb (it's a filter, not a navigation segment), but it IS
 * preserved in each href so the user's area selection stays sticky when
 * they click a breadcrumb. Tabell is dropped entirely — clicking back to
 * the chart variant is the right behaviour for a presentation toggle.
 */
export const getBreadcrumbs = (view: DayView, area: Omraade | null): Breadcrumb[] => {
	const areaSeg = area ? `/${area.seoSlug}` : "";

	const crumbs: Breadcrumb[] = [
		{ label: "Forside", href: "/" },
		{ label: "Spotprisen", href: `/spotprisen${areaSeg}` },
	];
	if (view) {
		crumbs.push({
			label: getDayLabel(view, true),
			href: `/spotprisen/${view}${areaSeg}`,
		});
	}
	return crumbs;
};
